import { EventsByStatusResponse, Sport, SportListResponse } from "@/types";

import { ListedEventStatus } from "../hooks/useEventsByStatus";
import { EVENT_STATUS_REFETCH_INTERVAL } from "./matchOdds";

type CountKey = "liveEventCount" | "next24EventCount" | "upcomingEventCount";
type TotalKey = "totalInPlayCount" | "totalNext24Count" | "totalUpcomingCount";

const KEYS: Record<ListedEventStatus, { count: CountKey; total: TotalKey }> = {
  live: { count: "liveEventCount", total: "totalInPlayCount" },
  next24: { count: "next24EventCount", total: "totalNext24Count" },
  upcoming: { count: "upcomingEventCount", total: "totalUpcomingCount" },
};

export const isListedStatus = (status: string): status is ListedEventStatus => status in KEYS;

/**
 * A cached list only while its page keeps it fresh: the sidebar holds the
 * cache open, so a list left behind would otherwise count forever.
 */
export const freshData = <T>(
  query: { data?: T; dataUpdatedAt: number },
  status: ListedEventStatus,
  now: number,
): T | undefined =>
  query.data && now - query.dataUpdatedAt < 2 * EVENT_STATUS_REFETCH_INTERVAL[status]
    ? query.data
    : undefined;

export interface SportCounts {
  sports: Sport[];
  totalInPlayCount: number;
  totalNext24Count: number;
  totalUpcomingCount: number;
}

/** One sport's list as the sport page has it loaded. */
export interface LoadedSportList {
  slug: string;
  status: ListedEventStatus;
  eventCount: number;
}

/**
 * The sidebar and tab counts, taken from the lists the pages show wherever
 * one is loaded.
 *
 * The sports list counts every event with any active price; the event lists
 * drop those whose markets the per-sport bookmaker settings filter out, so
 * the two disagreed (In-Play 9 beside a page of 3). A loaded list wins.
 */
export const alignSportCounts = (
  list: SportListResponse | undefined,
  byStatus: Partial<Record<ListedEventStatus, EventsByStatusResponse>>,
  sportList?: LoadedSportList,
): SportCounts => {
  const counts: SportCounts = {
    sports: (list?.sports ?? []).map((sport) => ({ ...sport })),
    totalInPlayCount: list?.totalInPlayCount ?? 0,
    totalNext24Count: list?.totalNext24Count ?? 0,
    totalUpcomingCount: list?.totalUpcomingCount ?? 0,
  };

  (Object.keys(KEYS) as ListedEventStatus[]).forEach((status) => {
    const data = byStatus[status];
    if (!data) return;
    const { count, total } = KEYS[status];
    const bySlug = new Map(data.sports.map((group) => [group.sport.slug, group.eventCount]));
    counts.sports.forEach((sport) => {
      sport[count] = bySlug.get(sport.slug) ?? 0;
    });
    counts[total] = data.eventCount;
  });

  if (sportList) {
    const { count, total } = KEYS[sportList.status];
    const sport = counts.sports.find((item) => item.slug === sportList.slug);
    if (sport) {
      counts[total] = Math.max(counts[total] + sportList.eventCount - (sport[count] ?? 0), 0);
      sport[count] = sportList.eventCount;
    }
  }

  counts.sports.forEach((sport) => {
    if (sport.liveEventCount == null && sport.next24EventCount == null) return;
    sport.eventsCount =
      (sport.liveEventCount ?? 0) + (sport.next24EventCount ?? 0) + (sport.upcomingEventCount ?? 0);
  });

  return counts;
};
